import ExcelJS from 'exceljs';

export class SkidExcelReport {
    /**
     * Generates and downloads an Excel report.
     * @param {Object} data - The data object.
     */
    static async exportToExcel(data: any) {
        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'AMV Monitor';
        workbook.created = new Date();
        const { worksite, machine } = data;
        let createFilename = new Date().toLocaleDateString() + '_' + machine.id + '_' + (worksite.projectName || 'Project') + '_' + (worksite.worksiteName || 'Worksite') + '_' + (worksite.tunnelName || 'Tunnel') + '_' + (worksite.peg || 'Peg') + '_' + (worksite.screen || 'Screen');
        const isSelected = (section: string) => data.selectedSections && data.selectedSections.includes(section);

        if (isSelected('Frontpage')) this.addFrontpageSheet(workbook, data);
        if (isSelected('Summary')) this.addSummarySheet(workbook, data);
        if (isSelected('Setup')) this.addSetupSheet(workbook, data);
        if (isSelected('Status')) this.addStatusSheet(workbook, data);
        if (isSelected('Material')) this.addMaterialSheet(workbook, data);
        if (isSelected('PumpInformation')) this.addPumpInformationSheet(workbook, data);
        if (isSelected('PumpRecipeGantt')) this.addPumpRecipeGanttSheet(workbook, data);
        if (isSelected('RecipeInformation')) this.addRecipeInformationSheet(workbook, data);
        if (isSelected('GeoAnalysis')) this.addGeoAnalysisSheet(workbook, data);
        if (isSelected('HoleGraphs')) this.addHoleGraphsSheets(workbook, data);
        if (isSelected('Events')) this.addEventsSheet(workbook, data);

        const buffer = await workbook.xlsx.writeBuffer();
        this._triggerDownload(buffer, createFilename + '.xlsx');
    }

    static addFrontpageSheet(workbook: ExcelJS.Workbook, data: any) {
        const sheet = workbook.addWorksheet('General Info');
        sheet.columns = [
            { header: '', key: '', width: 25 },
            { header: '', key: '', width: 25 },
            { header: '', key: '', width: 40 }
        ];

        const addSection = (title: string, items: { label: string, value: any }[]) => {
            sheet.addRow([title]);
            const row = sheet.lastRow;
            row!.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
            row!.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };

            items.forEach(item => {
                sheet.addRow(['', item.label, item.value || '-']);
            });
            sheet.addRow([]);
        };

        const { worksite, machine, timeInformation, totalUsedMaterialFromEvents, projectAdditionalData } = data;

        if (worksite) {
            addSection('Project Information', [
                { label: 'Project Name', value: worksite.projectName },
                { label: 'Worksite', value: worksite.worksiteName },
                { label: 'Tunnel', value: worksite.tunnelName },
                { label: 'Peg', value: worksite.peg },
                { label: 'Screen', value: worksite.screen }
            ]);
        }

        if (machine) {
            addSection('Machine Information', [
                { label: 'Machine ID', value: machine.id },
                { label: 'Machine Type', value: machine.type }
            ]);
        }

        if (timeInformation) {
            addSection('Production Time', [
                { label: 'Start Time', value: timeInformation.startTime },
                { label: 'Stop Time', value: timeInformation.stopTime },
                { label: 'Total Duration', value: timeInformation.totalTime }
            ]);
        }

        if (totalUsedMaterialFromEvents && totalUsedMaterialFromEvents.length > 0) {
            const m = totalUsedMaterialFromEvents[0];
            const hasData = m.cement1 > 0 || m.cement2 > 0 || m.additive1 > 0 || m.accelerator > 0;
            if (hasData) {
                addSection('Material Registered from Events', [
                    { label: 'Cement 1', value: `${m.cement1?.toFixed(2)} kg` },
                    { label: 'Cement 2', value: `${m.cement2?.toFixed(2)} kg` },
                    { label: 'Additive 1', value: `${m.additive1?.toFixed(2)} L` },
                    { label: 'Additive 2', value: `${m.additive2?.toFixed(2)} L` },
                    { label: 'Additive 3', value: `${m.additive3?.toFixed(2)} L` },
                    { label: 'Additive 4', value: `${m.additive4?.toFixed(2)} L` },
                    { label: 'Additive 5', value: `${m.additive5?.toFixed(2)} L` },
                    { label: 'Additive 6', value: `${m.additive6?.toFixed(2)} L` },
                    { label: 'Accelerator', value: `${m.accelerator?.toFixed(2)} L` }
                ]);
            }
        }

        if (projectAdditionalData) {
            const { wcTestData, additionalInfo } = projectAdditionalData;
            if (wcTestData) {
                sheet.addRow(['WC Test Results']);
                sheet.lastRow!.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
                sheet.lastRow!.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };

                const headers = ['Test Type', 'Test 1', 'Test 2', 'Test 3', 'Test 4'];
                const hdrRow = sheet.addRow(headers);
                hdrRow.eachCell({ includeEmpty: false }, cell => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                });

                sheet.addRow(['Marsh-viscosity', ...(wcTestData.marshViscosity || [])]);
                sheet.addRow(['Self-weight control', ...(wcTestData.selfWeightControl || [])]);
                sheet.addRow(['Water excretion', ...(wcTestData.waterExcretion || [])]);
                sheet.addRow(['Unbundling', ...(wcTestData.unbundling || [])]);
                sheet.addRow([]);
            }

            if (additionalInfo) {
                const infoItems = [];
                const labels = {
                    rigging: 'Up and Down Rigging',
                    startDrilling: 'Start Drilling',
                    curingTime: 'Curing Time',
                    boreMeter: 'Bore Meter',
                    usedPackers: 'Used Packers',
                    waterLeakage: 'Water Leakage',
                    waterLoss: 'Water Loss Measurements',
                    comments: 'Comments'
                };
                for (const [key, label] of Object.entries(labels)) {
                    if (additionalInfo[key] && additionalInfo[key].trim() !== '') {
                        infoItems.push({ label, value: additionalInfo[key] });
                    }
                }
                if (infoItems.length > 0) {
                    addSection('Additional Project Information', infoItems);
                }
            }
        }
    }

    static addSummarySheet(workbook: ExcelJS.Workbook, data: any) {
        const summaryData = data.totalConsumedMaterialOverviewFromPumpsData;
        if (summaryData && summaryData.length > 0) {
            const sheet = workbook.addWorksheet('Summary');
            let currentRow = 1;

            // Description row
            sheet.getCell(`A${currentRow}`).value = 'Material consumption per borehole — values calculated from pump meter readings. Cement in kg, additives and accelerator in L.';
            sheet.getCell(`A${currentRow}`).font = { italic: true, color: { argb: 'FF4B5E6D' } };
            sheet.getRow(currentRow).height = 18;
            currentRow += 2;

            const rows = summaryData.map((item: any) => [
                item.holeNo,
                item.maxPressure || 0,
                item.cement1 || 0,
                item.cement2 || 0,
                item.additive1 || 0,
                item.additive2 || 0,
                item.additive3 || 0,
                item.accelerator || 0
            ]);

            sheet.addTable({
                name: 'Summary',
                ref: `A${currentRow}`,
                headerRow: true,
                totalsRow: true,
                style: {
                    theme: 'TableStyleMedium2',
                    showRowStripes: true,
                },
                columns: [
                    { name: 'Hole No', totalsRowLabel: 'Total' },
                    { name: 'Max Pressure (bar)', totalsRowFunction: 'none' },
                    { name: 'Cement 1 (kg)', totalsRowFunction: 'sum' },
                    { name: 'Cement 2 (kg)', totalsRowFunction: 'sum' },
                    { name: 'Additive 1 (L)', totalsRowFunction: 'sum' },
                    { name: 'Additive 2 (L)', totalsRowFunction: 'sum' },
                    { name: 'Additive 3 (L)', totalsRowFunction: 'sum' },
                    { name: 'Accelerator (L)', totalsRowFunction: 'sum' }
                ],
                rows: rows,
            });
            sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            });

            sheet.getColumn(1).width = 10;
            sheet.getColumn(2).width = 20;
            [3, 4].forEach(i => { sheet.getColumn(i).width = 15; sheet.getColumn(i).numFmt = '0.0'; });
            [5, 6, 7, 8].forEach(i => { sheet.getColumn(i).width = 18; sheet.getColumn(i).numFmt = '0.00'; });
        }
    }

    static addSetupSheet(workbook: ExcelJS.Workbook, data: any) {
        const { setupData, recipeSequenceData } = data;
        if ((!setupData || setupData.length === 0) && (!recipeSequenceData || recipeSequenceData.length === 0)) return;

        const sheet = workbook.addWorksheet('Setup');
        let currentRow = 1;

        if (setupData && setupData.length > 0) {
            sheet.getCell(`A${currentRow}`).value = "Pump Setup History";
            sheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
            currentRow++;

            const tableRows = setupData.flat().map((s: any) => [
                s.timestamp ? new Date(s.timestamp).toLocaleString() : '',
                s.pumpId,
                Number(s.endPressure),
                Number(s.stableTime),
                Number(s.endAmount)
            ]);

            sheet.addTable({
                name: 'PumpSetupHistory',
                ref: `A${currentRow}`,
                headerRow: true,
                totalsRow: false,
                style: {
                    theme: 'TableStyleMedium2',
                    showRowStripes: true,
                },
                columns: [
                    { name: 'Time Stamp' },
                    { name: 'Pump ID' },
                    { name: 'End Pressure (bar)' },
                    { name: 'Stable Time (s)' },
                    { name: 'End Amount (L)' },
                ],
                rows: tableRows,
            });
            sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            });
            currentRow += tableRows.length + 1;
            currentRow += 2;
        }

        if (recipeSequenceData && recipeSequenceData.length > 0) {
            sheet.getCell(`A${currentRow}`).value = "Recipe & Sequence Information";
            sheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
            currentRow++;

            recipeSequenceData.forEach((r: any, index: number) => {
                sheet.getCell(`A${currentRow}`).value = `ID: ${r.recipeID} - ${r.recipeName}`;
                sheet.getCell(`A${currentRow}`).font = { bold: true };
                currentRow++;

                const time = r.timestamp ? new Date(r.timestamp).toLocaleString() : '';
                sheet.getCell(`A${currentRow}`).value = `VC: ${Number(r.vcValue || 0).toFixed(2)} | Vol: ${Number(r.volume || 0).toFixed(2)} L | Cem: ${Number(r.cement || 0).toFixed(2)} kg | Time: ${time}`;
                currentRow++;

                const visibleSteps = (r.sequence || []).filter((step: any) =>
                    step.materialName !== 'None' ||
                    step.volume !== 0 ||
                    step.mixingTime !== 0
                );

                if (visibleSteps.length > 0) {
                    const tableRows = visibleSteps.map((s: any) => [
                        s.sequenceNo,
                        s.materialName,
                        s.volume || 0,
                        Number(s.mixingTime)
                    ]);

                    sheet.addTable({
                        name: `RecipeSequence_${index}`,
                        ref: `A${currentRow}`,
                        headerRow: true,
                        totalsRow: false,
                        style: {
                            theme: 'TableStyleMedium2',
                            showRowStripes: true,
                        },
                        columns: [
                            { name: 'Step' },
                            { name: 'Material' },
                            { name: 'Volume (L)' },
                            { name: 'Mixing Time (s)' },
                        ],
                        rows: tableRows,
                    });
                    sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                    });
                    for (let i = 0; i < tableRows.length; i++) {
                        sheet.getRow(currentRow + 1 + i).getCell(3).numFmt = '0.00';
                    }
                    currentRow += tableRows.length + 1;
                }
                currentRow++;
            });
        }

        sheet.columns = [
            { width: 25 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }
        ];
    }

    static addStatusSheet(workbook: ExcelJS.Workbook, data: any) {
        const statusData = data.statusDataList;
        if (statusData && statusData.length > 0) {
            const sheet = workbook.addWorksheet('Hole Status');
            let currentRow = 1;

            // Description row
            sheet.getCell(`A${currentRow}`).value = 'Status and timing information for each borehole grouted during this session.';
            sheet.getCell(`A${currentRow}`).font = { italic: true, color: { argb: 'FF4B5E6D' } };
            sheet.getRow(currentRow).height = 18;
            currentRow += 2;

            // --- Calculations ---
            const total = statusData.length;
            const finishedCount = statusData.filter((i: any) => (i.eventType || '').toLowerCase().includes('reached')).length;
            const pendingCount = statusData.filter((i: any) => (i.eventType || '').toLowerCase().includes('not finished')).length;

            let totalSeconds = 0;
            statusData.forEach((item: any) => {
                if (item.totalTimeMs) {
                    totalSeconds += item.totalTimeMs / 1000;
                }
            });
            const avgSeconds = total > 0 ? totalSeconds / total : 0;

            const formatDurationSeconds = (seconds: number) => {
                const h = Math.floor(seconds / 3600);
                const m = Math.floor((seconds % 3600) / 60);
                const s = Math.floor(seconds % 60);
                const pad = (n: number) => n.toString().padStart(2, '0');
                return `${pad(h)}:${pad(m)}:${pad(s)}`;
            };

            // --- Overview Section ---
            sheet.getCell(`A${currentRow}`).value = "Hole Status Overview";
            sheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
            currentRow++;
            sheet.getRow(currentRow).values = ['Finished', 'Pending'];
            sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            });
            currentRow++;
            sheet.getRow(currentRow).values = [finishedCount, pendingCount];
            currentRow += 2;

            // --- Summary Section ---
            sheet.getCell(`A${currentRow}`).value = "Production Summary";
            sheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
            currentRow++;
            sheet.getRow(currentRow).values = ['Total Duration', 'Avg Duration'];
            sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            });
            currentRow++;
            sheet.getRow(currentRow).values = [formatDurationSeconds(totalSeconds), formatDurationSeconds(avgSeconds)];
            currentRow += 2;

            // --- Table Section ---
            sheet.getCell(`A${currentRow}`).value = "Hole Status Details";
            sheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
            currentRow++;

            const tableRows = statusData.map((s: any) => [
                s.holeNo,
                s.eventType,
                s.startTime ? new Date(s.startTime).toLocaleTimeString() : '-',
                s.stopTime ? new Date(s.stopTime).toLocaleTimeString() : '-',
                s.totalTimeMs ? formatDurationSeconds(s.totalTimeMs / 1000) : '-',
                s.volume || 0
            ]);

            sheet.addTable({
                name: 'HoleStatusDetails',
                ref: `A${currentRow}`,
                headerRow: true,
                totalsRow: false,
                style: {
                    theme: 'TableStyleMedium2',
                    showRowStripes: true,
                },
                columns: [
                    { name: 'Hole No' },
                    { name: 'Status' },
                    { name: 'Start Time' },
                    { name: 'Stop Time' },
                    { name: 'Duration (hh:mm:ss)' },
                    { name: 'Volume (L)' },
                ],
                rows: tableRows,
            });
            sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            });
            for (let i = 0; i < tableRows.length; i++) {
                sheet.getRow(currentRow + 1 + i).getCell(6).numFmt = '0.00';
            }
            currentRow += tableRows.length + 1;
            sheet.columns = [
                { width: 10 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 15 }, { width: 15, numFmt: '0.00' }
            ];
        }
    }

    static addMaterialSheet(workbook: ExcelJS.Workbook, data: any) {
        const {  totalUsedMaterialFromEvents, totalConsumedMaterialOverviewFromPumpsData } = data;
        const pumpsData = totalConsumedMaterialOverviewFromPumpsData;
        if (!totalUsedMaterialFromEvents && (!pumpsData || pumpsData.length === 0)) return;

        const sheet = workbook.addWorksheet('Material');
        let currentRow = 1;

        // Description row
        sheet.getCell(`A${currentRow}`).value = 'Total material consumption — compares event registrations (operator entries) with pump meter calculations.';
        sheet.getCell(`A${currentRow}`).font = { italic: true, color: { argb: 'FF4B5E6D' } };
        sheet.getRow(currentRow).height = 18;
        currentRow += 2;
        const extractTotalMaterialConsumedFromPumps = (() => {
            const total = {
                cement1: 0,
                cement2: 0,
                additive1: 0,
                additive2: 0,
                accelerator: 0,
            };
            for (const item of (totalConsumedMaterialOverviewFromPumpsData || []))
            {
                total.cement1 += item.cement1 || 0;
                total.cement2 += item.cement2 || 0;
                total.additive1 += item.additive1 || 0;
                total.additive2 += item.additive2 || 0;
                total.accelerator += item.accelerator || 0;
            }
            return total;
        })();

        const addTable = (title: string, model: any, isEvent: boolean) => {
            if (!model) return;
            sheet.getCell(`A${currentRow}`).value = title;
            sheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
            currentRow++;

            const headers = ['Material', 'Amount'];
            sheet.getRow(currentRow).values = headers;
            sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            });
            currentRow++;

            const pushRow = (label: string, val: any, unit: string) => {
                if (val && val > 0) {
                    sheet.getRow(currentRow).values = [label, `${val.toFixed(2)} ${unit}`];
                    currentRow++;
                }
            };

            if (isEvent) {
                pushRow('Cement 1', model.cement1, 'kg');
                pushRow('Cement 2', model.cement2, 'kg');
                pushRow('Additive 1', model.additive1, 'L');
                pushRow('Additive 2', model.additive2, 'L');
                pushRow('Additive 3', model.additive3, 'L');
                pushRow('Additive 4', model.additive4, 'L');
                pushRow('Additive 5', model.additive5, 'L');
                pushRow('Additive 6', model.additive6, 'L');
                pushRow('Accelerator', model.accelerator, 'L');
            } else {
                pushRow('Cement 1', model.cement1, 'kg');
                pushRow('Cement 2', model.cement2, 'kg');
                pushRow('Additive 1', model.additive1, 'L');
                pushRow('Additive 2', model.additive2, 'L');
                pushRow('Additive 3', model.additive3, 'L');
                pushRow('Additive 4', model.additive4, 'L');
                pushRow('Additive 5', model.additive5, 'L');
                pushRow('Additive 6', model.additive6, 'L');
                pushRow('Accelerator', model.accelerator, 'L');
            }
            currentRow += 2;
        };

        if (totalUsedMaterialFromEvents) addTable("Registered from Events", totalUsedMaterialFromEvents, true);
        if (extractTotalMaterialConsumedFromPumps) addTable("Calculated from Pumps", extractTotalMaterialConsumedFromPumps, false);

        if (totalConsumedMaterialOverviewFromPumpsData && totalConsumedMaterialOverviewFromPumpsData.length > 0) {
            sheet.getCell(`A${currentRow}`).value = "Consumption per Hole";
            sheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
            currentRow++;

            const tableRows = totalConsumedMaterialOverviewFromPumpsData.map((m: any) => [
                m.holeNo,
                m.maxPressure || 0,
                m.cement1 || 0,
                m.cement2 || 0,
                m.additive1 || 0,
                m.additive2 || 0,
                m.additive3 || 0,
                m.accelerator || 0
            ]);

            sheet.addTable({
                name: 'ConsumptionPerHole',
                ref: `A${currentRow}`,
                headerRow: true,
                totalsRow: false,
                style: {
                    theme: 'TableStyleMedium2',
                    showRowStripes: true,
                },
                columns: [
                    { name: 'Hole No' },
                    { name: 'Max Pressure (bar)' },
                    { name: 'Cement 1 (kg)' },
                    { name: 'Cement 2 (kg)' },
                    { name: 'Additive 1 (L)' },
                    { name: 'Additive 2 (L)' },
                    { name: 'Additive 3 (L)' },
                    { name: 'Accelerator (L)' },
                ],
                rows: tableRows,
            });
            sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            });

            for (let i = 0; i < tableRows.length; i++) {
                const row = sheet.getRow(currentRow + 1 + i);
                row.getCell(2).numFmt = '0.0';
                row.getCell(3).numFmt = '0.0';
                row.getCell(4).numFmt = '0.0';
                row.getCell(5).numFmt = '0.00';
                row.getCell(6).numFmt = '0.00';
                row.getCell(7).numFmt = '0.00';
                row.getCell(8).numFmt = '0.00';
            }
            currentRow += tableRows.length + 1;
        }
        sheet.getColumn(1).width = 10;
        sheet.getColumn(2).width = 20;
        [3, 4].forEach(i => sheet.getColumn(i).width = 15);
        [5, 6, 7, 8].forEach(i => sheet.getColumn(i).width = 18);
    }

    static addPumpInformationSheet(workbook: ExcelJS.Workbook, data: any) {
        const pumpInfo = data.pumpInformationDataList || data.pumpInfo;
        if (pumpInfo && pumpInfo.length > 0) {
            const sheet = workbook.addWorksheet('Pump Info');

            sheet.getCell('A1').value = 'Pump performance statistics for the complete grouting session — volumes, pressures, and operating time per pump.';
            sheet.getCell('A1').font = { italic: true, color: { argb: 'FF4B5E6D' } };
            sheet.getRow(1).height = 18;
            sheet.addRow([]);

            const formatValue = (header: string, value: any) => {
                if (value === "0" || value === "" || !value) return "—";
                const isDuration = ["Total Time", "Net Time", "Idle Time"].includes(header);
                if (isDuration) {
                    const totalDays = parseFloat(value);
                    if (isNaN(totalDays)) return value;
                    return this._formatDurationFromDays(totalDays);
                }
                return value;
            };

            const rows = pumpInfo.map((p: any) => [
                p.dataHeader,
                formatValue(p.dataHeader, p.pumpOne),
                formatValue(p.dataHeader, p.pumpTwo),
                formatValue(p.dataHeader, p.pumpThree),
                formatValue(p.dataHeader, p.pumpFour),
                formatValue(p.dataHeader, p.totalPumpTime)
            ]);

            sheet.addTable({
                name: 'PumpInfo',
                ref: 'A3',
                headerRow: true,
                totalsRow: false,
                style: {
                    theme: 'TableStyleMedium2',
                    showRowStripes: true,
                },
                columns: [
                    { name: 'Description' },
                    { name: 'Pump 1' },
                    { name: 'Pump 2' },
                    { name: 'Pump 3' },
                    { name: 'Pump 4' },
                    { name: 'Total' }
                ],
                rows: rows,
            });
            sheet.getRow(3).eachCell({ includeEmpty: false }, cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            });

            sheet.getColumn(1).width = 25;
            [2, 3, 4, 5, 6].forEach(i => sheet.getColumn(i).width = 20);

            const concurrent = data.ConcurrentPumpOverview;
            if (concurrent) {
                const total = concurrent.onePumpMs + concurrent.twoPumpMs + concurrent.threePumpMs + concurrent.fourPumpMs;
                const pct = (ms: number) => total > 0 ? ((ms / total) * 100).toFixed(1) + '%' : '—';
                const msToHHmmss = (ms: number) => {
                    const s = Math.floor(ms / 1000);
                    const pad = (n: number) => n.toString().padStart(2, '0');
                    return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
                };
                const twoPlusMs = concurrent.twoPumpMs + concurrent.threePumpMs + concurrent.fourPumpMs;
                const threePlusMs = concurrent.threePumpMs + concurrent.fourPumpMs;

                const startRow = rows.length + 5; // 2 description rows + 1 header + data + 1 gap
                const titleCell = sheet.getCell(`A${startRow}`);
                titleCell.value = 'Concurrent Pump Operation';
                titleCell.font = { bold: true, size: 12 };

                const hdrRow = startRow + 1;
                ['Description', 'Time', '% of Total'].forEach((h, i) => {
                    const cell = sheet.getCell(hdrRow, i + 1);
                    cell.value = h;
                    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                });

                const concRows = [
                    ['1 pump', concurrent.onePumpTime, pct(concurrent.onePumpMs)],
                    ['2 pumps simultaneously', concurrent.twoPumpTime, pct(concurrent.twoPumpMs)],
                    ['3 pumps simultaneously', concurrent.threePumpTime, pct(concurrent.threePumpMs)],
                    ['4 pumps simultaneously', concurrent.fourPumpTime, pct(concurrent.fourPumpMs)],
                    ['2+ pumps (total)', msToHHmmss(twoPlusMs), pct(twoPlusMs)],
                    ['3+ pumps (total)', msToHHmmss(threePlusMs), pct(threePlusMs)],
                ];
                concRows.forEach((row, rowIdx) => {
                    row.forEach((val, colIdx) => {
                        const cell = sheet.getCell(hdrRow + 1 + rowIdx, colIdx + 1);
                        cell.value = val;
                        if (rowIdx >= 4) cell.font = { bold: true };
                    });
                });
                sheet.getColumn(1).width = 30;
            }
        }
    }

    static addPumpRecipeGanttSheet(workbook: ExcelJS.Workbook, data: any) {
        const ganttData = data.pumprecipeGanttData;
        if (ganttData && ganttData.headers && ganttData.rows && ganttData.rows.length > 0) {
            const sheet = workbook.addWorksheet('Pump & Recipe Gantt');

            const headerRow = ['Hole No', ...ganttData.headers.map((h: any) => {
                const d = new Date(h);
                return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
            })];

            sheet.addRow(headerRow);
            sheet.getRow(1).font = { bold: true, size: 9 };

            ganttData.rows.forEach((row: any) => {
                const rowValues = [row.holeNo];
                row.cells.forEach(() => rowValues.push(''));
                sheet.addRow(rowValues);

                const currentRow = sheet.lastRow;

                row.cells.forEach((cell: any, index: number) => {
                    if (cell) {
                        const argb = this._pumpIdToArgb(cell.pumpId);
                        const cellRef = currentRow!.getCell(index + 2);
                        cellRef.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: argb }
                        };
                        cellRef.value = `${cell.pumpId} R${cell.recipeId}`;
                        cellRef.font = { color: { argb: 'FFFFFFFF' }, size: 8, bold: true };
                        cellRef.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                });
            });

            sheet.getColumn(1).width = 10;
            for (let i = 2; i <= headerRow.length; i++) {
                sheet.getColumn(i).width = 7;
            }
        }
    }

    static addRecipeInformationSheet(workbook: ExcelJS.Workbook, data: any) {
        const { recipeOverviewList } = data;
        if (!recipeOverviewList || recipeOverviewList.length === 0) return;
        const recipeStats = (() => {
            let data = recipeOverviewList;

            const grouped = new Map();
            for (const item of data) {
                const key = item.recipeId;
                if (!grouped.has(key)) {
                    grouped.set(key, {
                        recipeId: key,
                        recipeName: item.recipeName,
                        cement: 0,
                        accelerator: 0,
                        totalTimeMs: 0
                    });
                }
                const group = grouped.get(key);
                group.cement += (item.cement || 0);
                group.accelerator += (item.accelerator || 0);

                if (item.totalTimeMs) {
                    group.totalTimeMs += item.totalTimeMs;
                }
            }

            return Array.from(grouped.values()).map(item => {
                return {
                recipeId: item.recipeId,
                recipeName: item.recipeName,
                cement: item.cement,
                accelerator: item.accelerator,
                totalTimeMs: item.totalTimeMs,
                totalTime: this._calculateTotalTimeFromMs(item.totalTimeMs)
                }
            }).sort((a, b) => b.totalTimeMs - a.totalTimeMs)
        })();
        const sheet = workbook.addWorksheet('Recipe Info');
        let currentRow = 1;

        // Description row
        sheet.getCell(`A${currentRow}`).value = 'Recipe usage summary — total consumption and time per recipe, followed by a per-hole breakdown of each recipe run.';
        sheet.getCell(`A${currentRow}`).font = { italic: true, color: { argb: 'FF4B5E6D' } };
        sheet.getRow(currentRow).height = 18;
        currentRow += 2;

        let totalCement = 0;
        let totalAccelerator = 0;
        let totalMs = 0;

        if (recipeStats.length > 0) {
            recipeStats.forEach((item: any) => {
                totalCement += (item.cement || 0);
                totalAccelerator += (item.accelerator || 0);
                totalMs += item.totalTimeMs;
            });

            // Total Usage Table
            sheet.getCell(`A${currentRow}`).value = "Total Usage";
            sheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
            currentRow++;

            const totalHeaders = ['Metric', 'Value'];
            sheet.getRow(currentRow).values = totalHeaders;
            sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            });
            currentRow++;

            sheet.getRow(currentRow++).values = ['Total Runs', recipeOverviewList.length];
            sheet.getRow(currentRow++).values = ['Total Cement', `${totalCement.toFixed(2)} kg`];
            sheet.getRow(currentRow++).values = ['Total Accelerator', `${totalAccelerator.toFixed(2)} kg`];
            sheet.getRow(currentRow++).values = ['Total Duration', this._calculateTotalTimeFromMs(totalMs)];
            currentRow += 2;
        }

        if (recipeStats && recipeStats.length > 0) {
            if (recipeStats.length > 0) {
                sheet.getCell(`A${currentRow}`).value = "Consumption per Recipe";
                sheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
                currentRow++;

                const tableRows = recipeStats.map((r: any) => [
                    r.recipeName,
                    r.cement,
                    r.accelerator,
                    r.totalTime
                ]);

                sheet.addTable({
                    name: 'ConsumptionPerRecipe',
                    ref: `A${currentRow}`,
                    headerRow: true,
                    totalsRow: false,
                    style: {
                        theme: 'TableStyleMedium2',
                        showRowStripes: true,
                    },
                    columns: [
                        { name: 'Recipe Name' },
                        { name: 'Cement (kg)' },
                        { name: 'Accelerator (kg)' },
                        { name: 'Total Time' },
                    ],
                    rows: tableRows,
                });
                sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                });

                for (let i = 0; i < tableRows.length; i++) {
                    const row = sheet.getRow(currentRow + 1 + i);
                    row.getCell(2).numFmt = '0.0';
                    row.getCell(3).numFmt = '0.0';
                }
                currentRow += tableRows.length + 1;
            }
            currentRow += 2;
        }

        if (recipeOverviewList && recipeOverviewList.length > 0) {
            sheet.getCell(`A${currentRow}`).value = "Recipe Overview";
            sheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
            currentRow++;

            const tableRows = recipeOverviewList.map((r: any) => [
                r.holeNo,
                r.recipeName,
                r.startTime ? new Date(r.startTime).toLocaleTimeString() : '',
                r.stopTime ? new Date(r.stopTime).toLocaleTimeString() : '',
                r.totalTime,
                r.cement || 0,
                r.accelerator || 0
            ]);

            const tableStartRow = currentRow;

            sheet.addTable({
                name: 'RecipeOverview',
                ref: `A${currentRow}`,
                headerRow: true,
                totalsRow: false,
                style: {
                    theme: 'TableStyleMedium2',
                    showRowStripes: true,
                },
                columns: [
                    { name: 'Hole No' },
                    { name: 'Recipe Name' },
                    { name: 'Start Time' },
                    { name: 'Stop Time' },
                    { name: 'Duration (hh:mm:ss)' },
                    { name: 'Cement (kg)' },
                    { name: 'Accelerator (kg)' },
                ],
                rows: tableRows,
            });
            sheet.getRow(tableStartRow).eachCell({ includeEmpty: false }, cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            });

            for (let i = 0; i < tableRows.length; i++) {
                const row = sheet.getRow(tableStartRow + 1 + i);
                row.getCell(6).numFmt = '0.0';
                row.getCell(7).numFmt = '0.0';
            }
            currentRow += tableRows.length + 1;
        }
        sheet.columns.forEach(c => c.width = 20);
    }

    static addGeoAnalysisSheet(workbook: ExcelJS.Workbook, data: any) {
        const items: any[] = data.totalConsumedMaterialOverviewFromPumpsData || [];
        if (!items.length) return;

        const PRESSURE_LOW = 10;
        const resolveThreshold = (vals: number[]): number => {
            const freq = new Map<number, number>();
            vals.forEach(v => freq.set(v, (freq.get(v) || 0) + 1));
            const maxFreq = Math.max(...freq.values());
            const top = [...freq.entries()].filter(([, f]) => f === maxFreq).map(([v]) => v);
            if (top.length === 1) return top[0];
            const s = [...vals].sort((a, b) => a - b);
            const m = Math.floor(s.length / 2);
            return s.length % 2 === 0 ? Math.round((s[m - 1] + s[m]) / 2) : s[m];
        };
        const setupSessions: any[][] = data.setupData || [];
        let PRESSURE_HIGH = 25;
        let maxVolumeLimit = 0; // endAmount in litres — different unit to cement kg
        if (setupSessions.length) {
            const latestSession = [...setupSessions].sort((a: any[], b: any[]) => {
                const tA = a.length ? new Date(a[0].timestamp).getTime() : 0;
                const tB = b.length ? new Date(b[0].timestamp).getTime() : 0;
                return tB - tA;
            })[0];
            const pressVals = (latestSession || []).map((c: any) => c.endPressure).filter((v: number) => v > 0);
            if (pressVals.length) PRESSURE_HIGH = resolveThreshold(pressVals);
            const volVals = (latestSession || []).map((c: any) => c.endAmount).filter((v: number) => v > 0);
            if (volVals.length) maxVolumeLimit = resolveThreshold(volVals);
        }
        const getZone = (p: number) => p >= PRESSURE_HIGH ? 'High' : p >= PRESSURE_LOW ? 'Moderate' : 'Low';
        const ZONE_ARGB: Record<string, string> = {
            High:     'FF4CAF50',  // green
            Moderate: 'FFF0B323',  // amber
            Low:      'FFE53935',  // red
        };

        const scatterData = items
            .map((item: any) => ({
                holeNo:   item.holeNo,
                cement:   (item.cement1 || 0) + (item.cement2 || 0),
                pressure: item.maxPressure || 0,
            }))
            .filter((d: any) => d.cement > 0 || d.pressure > 0)
            .sort((a: any, b: any) => b.pressure - a.pressure);

        if (!scatterData.length) return;

        const statusList: any[] = data.statusDataList || [];
        const stopReasonMap = new Map<number, string>();
        statusList.forEach((s: any) => stopReasonMap.set(s.holeNo, s.eventType || ''));
        const STOP_ARGB = (et: string): string => {
            const s = (et || '').toLowerCase();
            if (s.includes('reached')) return 'FF4CAF50';
            if (s.includes('not finalized') || s.includes('working')) return 'FFF0B323';
            if (s.includes('error') || s.includes('stop')) return 'FFE53935';
            return 'FF607D8B';
        };

        const pressures = scatterData.map((d: any) => d.pressure);
        const cements   = scatterData.map((d: any) => d.cement);
        const sum       = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

        const sheet = workbook.addWorksheet('Geo Analysis');
        let currentRow = 1;

        // Description
        sheet.getCell(`A${currentRow}`).value = 'Ground condition analysis — max pressure vs total cement per borehole.';
        sheet.getCell(`A${currentRow}`).font = { italic: true, color: { argb: 'FF4B5E6D' } };
        sheet.getRow(currentRow).height = 15;
        currentRow++;
        sheet.getCell(`A${currentRow}`).value = `Zones: Low <${PRESSURE_LOW} bar  ·  Moderate ${PRESSURE_LOW}–${PRESSURE_HIGH} bar  ·  High ≥${PRESSURE_HIGH} bar${maxVolumeLimit > 0 ? `  ·  Max volume limit: ${maxVolumeLimit} L per pump` : ''}`;
        sheet.getCell(`A${currentRow}`).font = { italic: true, size: 9, color: { argb: 'FF4B5E6D' } };
        sheet.getRow(currentRow).height = 15;
        currentRow += 2;

        // KPI header
        sheet.getRow(currentRow).values = ['Holes Analysed', 'Peak Pressure (bar)', 'Avg Pressure (bar)', 'Total Cement (kg)', `High Zone (≥${PRESSURE_HIGH} bar)`, 'Low Zone (<10 bar)'];
        sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { horizontal: 'center' };
        });
        currentRow++;

        // KPI values
        sheet.getRow(currentRow).values = [
            scatterData.length,
            +Math.max(...pressures).toFixed(1),
            +(sum(pressures) / pressures.length).toFixed(1),
            +sum(cements).toFixed(0),
            scatterData.filter((d: any) => d.pressure >= PRESSURE_HIGH).length,
            scatterData.filter((d: any) => d.pressure < PRESSURE_LOW).length,
        ];
        sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
            cell.alignment = { horizontal: 'center' };
            cell.font = { bold: true, size: 12 };
        });
        currentRow += 2;

        // Material by Zone
        const totalCement = sum(cements);
        const zoneBreakdown = (['High', 'Moderate', 'Low'] as const).map(zone => {
            const zoneHoles = scatterData.filter((d: any) => getZone(d.pressure) === zone);
            const zoneCement = sum(zoneHoles.map((d: any) => d.cement));
            return {
                zone,
                holes: zoneHoles.length,
                cement: +zoneCement.toFixed(0),
                pct:    totalCement > 0 ? +(zoneCement / totalCement * 100).toFixed(1) : 0,
                avg:    zoneHoles.length > 0 ? +(zoneCement / zoneHoles.length).toFixed(1) : 0,
            };
        });

        sheet.getCell(`A${currentRow}`).value = 'Material by Zone';
        sheet.getCell(`A${currentRow}`).font = { bold: true, size: 11, color: { argb: 'FF4B5E6D' } };
        currentRow++;

        sheet.getRow(currentRow).values = ['Zone', 'Holes', 'Total Cement (kg)', '% of Total', 'Avg kg / Hole'];
        sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { horizontal: 'center' };
        });
        currentRow++;

        zoneBreakdown.forEach(row => {
            sheet.getRow(currentRow).values = [row.zone, row.holes, row.cement, row.pct, row.avg];
            sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
                cell.alignment = { horizontal: 'center' };
            });
            sheet.getCell(currentRow, 1).font = { bold: true, color: { argb: ZONE_ARGB[row.zone] } };
            currentRow++;
        });
        currentRow++;

        // Per-hole table
        sheet.addTable({
            name: 'GeoAnalysisDetail',
            ref: `A${currentRow}`,
            headerRow: true,
            totalsRow: false,
            style: { theme: 'TableStyleMedium2', showRowStripes: true },
            columns: [
                { name: 'Hole No' },
                { name: 'Total Cement (kg)' },
                { name: 'Max Pressure (bar)' },
                { name: 'Zone' },
                { name: 'Stop Reason' },
            ],
            rows: scatterData.map((d: any) => [
                d.holeNo,
                +d.cement.toFixed(1),
                +d.pressure.toFixed(1),
                getZone(d.pressure),
                stopReasonMap.get(d.holeNo) || '',
            ]),
        });

        // Style header
        sheet.getRow(currentRow).eachCell({ includeEmpty: false }, cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        });

        // Colour-code Zone and Stop Reason cells
        const dataStartRow = currentRow + 1;
        scatterData.forEach((d: any, i: number) => {
            const zoneCell = sheet.getCell(dataStartRow + i, 4);
            zoneCell.font = { bold: true, color: { argb: ZONE_ARGB[getZone(d.pressure)] } };

            const stopReason = stopReasonMap.get(d.holeNo) || '';
            if (stopReason) {
                const stopCell = sheet.getCell(dataStartRow + i, 5);
                stopCell.font = { bold: true, color: { argb: STOP_ARGB(stopReason) } };
            }
        });

        sheet.columns = [
            { width: 12 }, { width: 20 }, { width: 22 }, { width: 14 }, { width: 22 },
        ];
    }

    static addHoleGraphsSheets(workbook: ExcelJS.Workbook, data: any) {
        const holeGraphData = data.holeGraphData;
        if (!holeGraphData || holeGraphData.length === 0) return;

        holeGraphData.forEach((hole: any) => {
            if (!hole.data || hole.data.length === 0) return;

            const sheetName = `Hole ${hole.holeNo}`.substring(0, 31);
            const sheet = workbook.addWorksheet(sheetName);

            sheet.getCell('A1').value = `Time-series data for borehole ${hole.holeNo} — flow rate, pressure, cumulative volume and cement consumption logged during grouting.`;
            sheet.getCell('A1').font = { italic: true, color: { argb: 'FF4B5E6D' } };
            sheet.getRow(1).height = 18;
            sheet.addRow([]);

            sheet.addTable({
                name: `Hole${hole.holeNo}Data`,
                ref: 'A3',
                headerRow: true,
                totalsRow: false,
                style: { theme: 'TableStyleMedium2', showRowStripes: true },
                columns: [
                    { name: 'Timestamp' },
                    { name: 'Flow (L/min)' },
                    { name: 'Pressure (bar)' },
                    { name: 'Volume (L)' },
                    { name: 'Cement (kg)' },
                ],
                rows: hole.data.map((p: any) => [
                    p.timestamp ? new Date(p.timestamp).toLocaleString() : '',
                    p.flow ?? 0,
                    p.pressure ?? 0,
                    p.volume ?? 0,
                    p.cement ?? 0,
                ]),
            });

            sheet.getRow(3).eachCell({ includeEmpty: false }, cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            });

            sheet.getColumn(1).width = 22;
            sheet.getColumn(2).width = 15;
            sheet.getColumn(3).width = 15;
            sheet.getColumn(4).width = 12;
            sheet.getColumn(5).width = 12;
        });
    }

    static addEventsSheet(workbook: ExcelJS.Workbook, data: any) {
        const eventData = data.events;
        if (eventData && eventData.length > 0) {
            const sheet = workbook.addWorksheet('Events');

            sheet.getCell('A1').value = 'Registered events with cumulative material totals at time of event — useful for comparing operator entries with pump meter readings.';
            sheet.getCell('A1').font = { italic: true, color: { argb: 'FF4B5E6D' } };
            sheet.getRow(1).height = 18;
            sheet.addRow([]);

            const tableRows = eventData.map((e: any) => [
                e.timestamp ? new Date(e.timestamp).toLocaleString() : '',
                e.eventType || e.eventTypeId,
                e.eventUnit || e.eventUnitId,
                e.totalCementOne || 0,
                e.totalCementTwo || 0,
                e.totalAdditiveOne || 0,
                e.totalAdditiveTwo || 0,
                e.totalAdditiveThree || 0,
                e.totalAccelerator || 0,
                e.totalColloidal || 0,
                e.totalColloidalSlurry || 0,
                e.totalWater || 0,
                e.powerConsumptionskWh || 0
            ]);

            sheet.addTable({
                name: 'Events',
                ref: 'A3',
                headerRow: true,
                totalsRow: false,
                style: {
                    theme: 'TableStyleMedium2',
                    showRowStripes: true,
                },
                columns: [
                    { name: 'Time' }, { name: 'Event Type' }, { name: 'Unit' },
                    { name: 'Cement 1 (kg)' }, { name: 'Cement 2 (kg)' },
                    { name: 'Additive 1 (L)' }, { name: 'Additive 2 (L)' }, { name: 'Additive 3 (L)' },
                    { name: 'Accelerator (L)' }, { name: 'Colloidal (kg)' }, { name: 'Slurry (L)' },
                    { name: 'Water (L)' }, { name: 'Power (kWh)' }
                ],
                rows: tableRows,
            });
            sheet.getRow(3).eachCell({ includeEmpty: false }, cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF607D8B' } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            });

            sheet.getColumn(1).width = 20;
            sheet.getColumn(2).width = 25;
            sheet.getColumn(3).width = 10;
            for (let i = 4; i <= 13; i++) {
                sheet.getColumn(i).width = 16;
                sheet.getColumn(i).numFmt = '0.0';
            }
        }
    }

    static _styleHeader(sheet: ExcelJS.Worksheet) {
        const row = sheet.getRow(1);
        row.font = { bold: true };
        row.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFCCCCCC' }
        };
    }

    static _triggerDownload(buffer: ArrayBuffer, filename: string) {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(link.href);
    }

    static _formatDurationFromDays(totalDays: number): string {
        if (!totalDays || isNaN(totalDays)) return "00:00:00";
        let totalSeconds = Math.round(totalDays * 86400);
        const hours = Math.floor(totalSeconds / 3600);
        totalSeconds %= 3600;
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }

    static _pumpIdToArgb(pumpId: number): string {
        const hue = (pumpId * 60) % 360;
        return this._hslToArgb(hue, 70, 50);
    }

    static _recipeIdToArgb(recipeId: number): string {
        const hue = (recipeId * 137.508) % 360;
        return this._hslToArgb(hue, 70, 50);
    }

    static _hslToArgb(h: number, s: number, l: number): string {
        s /= 100;
        l /= 100;
        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs((h / 60) % 2 - 1));
        const m = l - c / 2;
        let r = 0, g = 0, b = 0;

        if (0 <= h && h < 60) { r = c; g = x; b = 0; }
        else if (60 <= h && h < 120) { r = x; g = c; b = 0; }
        else if (120 <= h && h < 180) { r = 0; g = c; b = x; }
        else if (180 <= h && h < 240) { r = 0; g = x; b = c; }
        else if (240 <= h && h < 300) { r = x; g = 0; b = c; }
        else if (300 <= h && h < 360) { r = c; g = 0; b = x; }

        const toHex = (n: number) => {
            const hex = Math.round((n + m) * 255).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        };

        return 'FF' + toHex(r) + toHex(g) + toHex(b);
    }

    static _calculateTotalTimeFromMs = (totalTimeMs: number) => {
        const hours = Math.floor(totalTimeMs / 3600000).toString().padStart(2, '0');
        const mins = Math.floor((totalTimeMs % 3600000) / 60000).toString().padStart(2, '0');
        const secs = Math.floor((totalTimeMs % 60000) / 1000).toString().padStart(2, '0');
        return `${hours}:${mins}:${secs}`;
    };
}